import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Empty,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  StockOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import PageLayout from '../../SharedComponents/PageLayout';
import { showAddNewServiceModal, showEditServiceModal } from '../../store/app-ui.store';
import { getAllVehicles, getVehicles } from '../../store/vehicle.store';
import {
  getAllServiceRecords,
  deleteServiceRecord,
  getServiceRecordsData,
  getServiceRecordsLoading,
  getSelectedVehicleId,
  setSelectedVehicleId,
} from '../../store/service.store';
import { DEFAULT_PAGE_SIZE } from '../../Utils/constants';
import { ServiceStatus, ServiceRecordDTO } from '../../Types/ServiceRecord';
import AddNewServiceRecord, { EditServiceModal } from '../../SharedComponents/Models/AddNewServiceRecord';

const { Text, Title } = Typography;
const { Option } = Select;

const normalizeStatus = (s?: string): ServiceStatus | null => {
  if (!s) return null;
  const u = s.toUpperCase().replace(/[\s_]+/g, ' ');
  if (u === 'OVERDUE') return 'OVERDUE';
  if (u === 'DUE SOON') return 'DUE SOON';
  if (u === 'OK') return 'OK';
  return null;
};

const strokeColorOf = (status: ServiceStatus): string => {
  if (status === 'OVERDUE') return '#f5222d';
  if (status === 'DUE SOON') return '#faad14';
  return '#52c41a';
};

const tagColorOf = (status: ServiceStatus): string => {
  if (status === 'OVERDUE') return 'red';
  if (status === 'DUE SOON') return 'orange';
  return 'green';
};

const fmtDate = (d: string | null) => (d ? moment(d).format('DD/MM/YYYY') : '—');

const ServiceManagement = (): JSX.Element => {
  const [viewRecord, setViewRecord] = useState<ServiceRecordDTO | null>(null);
  const dispatch = useAppDispatch();

  const records = useAppSelector(getServiceRecordsData);
  const loading = useAppSelector(getServiceRecordsLoading);
  const selectedVehicleId = useAppSelector(getSelectedVehicleId);

  const vehiclesRaw: any = useAppSelector(getVehicles);
  const vehicles: any[] = Array.isArray(vehiclesRaw) ? vehiclesRaw : vehiclesRaw?.data ?? [];

  useEffect(() => {
    dispatch(getAllVehicles({ page: 0, page_size: 100 }));
  }, [dispatch]);

  const vehicleById = useMemo(() => {
    const map: Record<number, any> = {};
    vehicles.forEach((v) => {
      map[v.vehicle_id] = v;
    });
    return map;
  }, [vehicles]);

  const handleVehicleChange = (vehicleId: number) => {
    dispatch(setSelectedVehicleId(vehicleId));
    dispatch(getAllServiceRecords(vehicleId));
  };

  const plateOf = (r: ServiceRecordDTO) => vehicleById[r.vehicle_id]?.plateNumber ?? String(r.vehicle_id);

  const kmLeftOf = (r: ServiceRecordDTO) => (r.next_service_odo_meter_reading_km ?? 0) - (r.odo_meter_reading_km ?? 0);

  const sortedRecords = useMemo(
    () =>
      [...records].sort((a, b) => {
        const dateA = a.serviced_date ? new Date(a.serviced_date).getTime() : 0;
        const dateB = b.serviced_date ? new Date(b.serviced_date).getTime() : 0;
        return dateB - dateA;
      }),
    [records],
  );

  const newestRecordId = sortedRecords[0]?.id;

  const statusOf = (r: ServiceRecordDTO): ServiceStatus => {
    if (r.id === newestRecordId) {
      return normalizeStatus(vehicleById[r.vehicle_id]?.service_status) ?? 'OK';
    }
    return 'OK';
  };

  const columns: ColumnsType<ServiceRecordDTO> = [
    {
      title: 'Vehicle Number',
      key: 'vehicleNumber',
      render: (_, r) => <Text strong>{plateOf(r)}</Text>,
    },
    {
      title: 'Current Meter Reading',
      dataIndex: 'odo_meter_reading_km',
      key: 'odo_meter_reading_km',
      render: (v: number) => `${(v ?? 0).toLocaleString()} km`,
      sorter: (a, b) => a.odo_meter_reading_km - b.odo_meter_reading_km,
    },
    {
      title: 'Last Service Date',
      dataIndex: 'serviced_date',
      key: 'serviced_date',
      render: (v) => fmtDate(v),
    },
    {
      title: 'Next Service Date',
      dataIndex: 'next_service_date',
      key: 'next_service_date',
      render: (v) => fmtDate(v),
    },
    {
      title: 'Next Service Meter Reading',
      dataIndex: 'next_service_odo_meter_reading_km',
      key: 'next_service_odo_meter_reading_km',
      render: (v: number | null) => (v === null || v === undefined ? '—' : `${v.toLocaleString()} km`),
    },
    {
      title: 'Remaining KM',
      key: 'kmLeft',
      sorter: (a, b) => kmLeftOf(a) - kmLeftOf(b),
      render: (_, r) => {
        const kmLeft = kmLeftOf(r);
        const status = statusOf(r);
        const target = r.next_service_odo_meter_reading_km ?? 0;
        const percent = target > 0 ? Number(((Math.max(kmLeft, 0) / target) * 100).toFixed(0)) : 0;
        return (
          <Progress
            percent={percent}
            size="small"
            strokeColor={strokeColorOf(status)}
            format={() => `${kmLeft.toLocaleString()}`}
          />
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, r) => {
        if (r.id === newestRecordId) {
          const s = statusOf(r);
          return <Tag color={tagColorOf(s)}>{s}</Tag>;
        }
        return <Tag color="default">Marked as Serviced</Tag>;
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, r) => (
        <Space size={8}>
          <Tooltip title="Edit Record">
            <Button type="text" icon={<EditOutlined />} onClick={() => dispatch(showEditServiceModal(r))} />
          </Tooltip>
          <Popconfirm
            title="Delete this service record?"
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => dispatch(deleteServiceRecord({ id: r.id, vehicleId: r.vehicle_id }))}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
          <Tooltip title="View Details">
            <Button type="text" icon={<EyeOutlined />} onClick={() => setViewRecord(r)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const totalVehicles = Array.isArray(vehiclesRaw) ? vehicles.length : vehiclesRaw?.total ?? vehicles.length;
  const dueCount = vehicles.filter((v) => normalizeStatus(v.service_status) === 'DUE SOON').length;
  const overdueCount = vehicles.filter((v) => normalizeStatus(v.service_status) === 'OVERDUE').length;
  const okCount = vehicles.filter((v) => normalizeStatus(v.service_status) === 'OK').length;
  const selectedVehicle = selectedVehicleId ? vehicleById[selectedVehicleId] : null;
  const nextServiceDate = selectedVehicle?.next_service_date ?? null;

  return (
    <PageLayout
      title="Service Management"
      actions={[
        <Button key="add" type="primary" onClick={() => dispatch(showAddNewServiceModal(true))}>
          Add Service Record
        </Button>,
      ]}
    >
      <div className="ops-metrics-grid">
        {[
          {
            label: 'Total Vehicles',
            value: `${totalVehicles}`,
            icon: <StockOutlined />,
          },
          {
            label: 'Due for Service',
            value: `${dueCount}`,
            icon: <BellOutlined />,
          },
          {
            label: 'Overdue for Service',
            value: `${overdueCount}`,
            icon: <WarningOutlined />,
            danger: true,
          },
          {
            label: 'Service OK',
            value: `${okCount}`,
            icon: <CheckCircleOutlined />,
          },
          {
            label: 'Next Service Date',
            value: nextServiceDate ? fmtDate(nextServiceDate) : '—',
            icon: <CalendarOutlined />,
          },
        ].map(({ label, value, icon, danger }) => (
          <Card key={label} bodyStyle={{ padding: '10px 12px' }}>
            <div className="ops-metrics-grid__header">
              <span className="ops-metrics-grid__label">{label}</span>
              <span className="ops-metrics-grid__icon">{icon}</span>
            </div>
            <div className={`ops-metrics-grid__value ${danger ? 'ops-metrics-grid__value--danger' : ''}`}>{value}</div>
          </Card>
        ))}
      </div>

      <Card>
        <div className="service-records-header">
          <Title level={5}>Vehicle Service Records</Title>
          <Space size={12}>
            <Select
              placeholder="Select a vehicle"
              value={selectedVehicleId}
              onChange={handleVehicleChange}
              style={{ width: 200 }}
              showSearch
              optionFilterProp="children"
            >
              {vehicles.map((v: any) => (
                <Option key={v.vehicle_id} value={v.vehicle_id}>
                  {v.plateNumber}
                </Option>
              ))}
            </Select>
          </Space>
        </div>

        {!selectedVehicleId ? (
          <Empty style={{ padding: '40px 0' }} description="Select a vehicle above to view its service records" />
        ) : (
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={sortedRecords}
            pagination={{
              pageSize: DEFAULT_PAGE_SIZE,
              showTotal: (t, range) => (
                <Text style={{ color: '#64748B' }}>
                  Showing {range[0]} to {range[1]} of {t} entries
                </Text>
              ),
            }}
          />
        )}
      </Card>

      <Modal
        visible={!!viewRecord}
        title="Service Record Details"
        footer={null}
        onCancel={() => setViewRecord(null)}
        destroyOnClose
      >
        {viewRecord && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Vehicle Number">
              <Text strong>{plateOf(viewRecord)}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Current Meter Reading">
              {(viewRecord.odo_meter_reading_km ?? 0).toLocaleString()} km
            </Descriptions.Item>
            <Descriptions.Item label="Service Date">{fmtDate(viewRecord.serviced_date)}</Descriptions.Item>
            <Descriptions.Item label="Next Service Date">{fmtDate(viewRecord.next_service_date)}</Descriptions.Item>
            <Descriptions.Item label="Next Service Meter Reading">
              {viewRecord.next_service_odo_meter_reading_km !== null &&
              viewRecord.next_service_odo_meter_reading_km !== undefined
                ? `${viewRecord.next_service_odo_meter_reading_km.toLocaleString()} km`
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>

      <AddNewServiceRecord
        onSuccess={(vehicleId) => {
          dispatch(setSelectedVehicleId(vehicleId));
          dispatch(getAllServiceRecords(vehicleId));
        }}
      />
      <EditServiceModal />
    </PageLayout>
  );
};

export default ServiceManagement;
